import { Switch } from "antd";
import { get, isEmpty, set } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineInsertComment } from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { SUBSCRIPTION_FORM_KEYS } from "utils/constants";

const Information = ({
  values,
  setFieldValue,
  errors,
  initialValues,
  formProps,
  setPercent,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const INFORMATION = t(
    "SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.INFORMATION",
    {
      returnObjects: true,
    },
  );
  const [isPPE, setIsPPE] = useState(
    get(initialValues, "project.fields.isPPE"),
  );
  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION}-section`}
    >
      <Form type="vertical" title={INFORMATION.TITLE}>
        <div className="grid--1">
          <div className="d-flex justify--between">
            <span className="contact-stepper__content--radio--title">
              {"Personne Politiquement Exposée (PPE*)"}
            </span>
            <Switch
              className="contact-stepper__content--radio--switch button__switch"
              checkedChildren={INFORMATION.OPTIONS.YES}
              unCheckedChildren={INFORMATION.OPTIONS.NO}
              defaultChecked={
                get(initialValues, "project.fields.isPPE") || false
              }
              onChange={(e) => {
                setIsPPE(e);
                set(initialValues, "project.fields.isPPE", e);
                if (!e) {
                  set(initialValues, "project.fields.ppeFunction", "");
                  set(initialValues, "project.fields.ppeRelated", false);
                }
                setPercent(initialValues);
              }}
            />
          </div>
          {/* PPE Information Alert */}
          <Alert
            showIcon
            type="info"
            message={
              "* PPE : Personne considérée comme exposée à des risques plus élevés de blanchiment de capitaux, en raison des fonctions politiques, juridictionnelles ou administratives qu'elle exerce ou a exercé au cours des 12 derniers mois."
            }
          />
          {/* PPE Function field - only shown if isPPE is true */}
          {isPPE && (
            <div>
              <Input
                label={"Quelles sont / étaient les fonctions du prospect ?"}
                placeholder={"Saisir la fonction du prospect"}
                defaultValue={get(initialValues, "project.fields.ppeFunction")}
                errors={errors["project.fields.ppeFunction"]}
                touched={!isEmpty(errors["project.fields.ppeFunction"])}
                onChange={({ target: { value } }) => {
                  set(initialValues, "project.fields.ppeFunction", value);
                }}
                onBlur={() => setPercent(initialValues)}
              />
            </div>
          )}

          {/* PPE Related toggle */}
          <div className="d-flex justify--between">
            <span className="contact-stepper__content--radio--title">
              {
                "Lien familial ou association étroite du prospect avec une PPE* ?"
              }
            </span>
            <Switch
              className="contact-stepper__content--radio--switch button__switch"
              checkedChildren={INFORMATION.OPTIONS.YES}
              unCheckedChildren={INFORMATION.OPTIONS.NO}
              defaultChecked={
                get(initialValues, "project.fields.ppeRelated") || false
              }
              onChange={(e) => {
                set(initialValues, "project.fields.ppeRelated", e);
                setPercent(initialValues);
              }}
            />
          </div>

          {/* Consent Switches */}
          <div className="d-flex justify--between">
            <span className="contact-stepper__content--radio--title">
              {
                "Le prospect déclare avoir reçu et pris connaissance, préalablement à l’adhésion au contrat, du document d’information sur le produit d’assurance complémentaire santé, des statuts, du règlement mutualiste, des dispositions particulières, ainsi que de la notice d'information relative au service MédecinDirect, et en accepter l’ensemble des termes."
              }
            </span>
            <Switch
              className="contact-stepper__content--radio--switch button__switch"
              checkedChildren={INFORMATION.OPTIONS.YES}
              unCheckedChildren={INFORMATION.OPTIONS.NO}
              defaultChecked={
                get(initialValues, "project.fields.acceptProductStatuses") ||
                false
              }
              onChange={(e) => {
                set(initialValues, "project.fields.acceptProductStatuses", e);
                setPercent(initialValues);
              }}
            />
          </div>
          <div className="d-flex justify--between">
            <span className="contact-stepper__content--radio--title">
              {
                "Le prospect atteste sur l’honneur de l’exactitude des renseignements complétés."
              }
            </span>
            <Switch
              className="contact-stepper__content--radio--switch button__switch"
              checkedChildren={INFORMATION.OPTIONS.YES}
              unCheckedChildren={INFORMATION.OPTIONS.NO}
              defaultChecked={
                get(initialValues, "project.fields.acceptAgreement") || false
              }
              onChange={(e) => {
                set(initialValues, "project.fields.acceptAgreement", e);
                setPercent(initialValues);
              }}
            />
          </div>
          <div className="d-flex justify--between">
            <span className="contact-stepper__content--radio--title">
              {`Le prospect est informé que la communication des documents contractuels ainsi que les informations relatives à l’exécution de son contrat d’assurance (communications, avis d’échéance, etc.) est réalisée exclusivement par voie électronique.
Il atteste que l’adresse e-mail transmise est valide et lui permet de recevoir ces informations.`}
            </span>
            <Switch
              className="contact-stepper__content--radio--switch button__switch"
              checkedChildren={INFORMATION.OPTIONS.YES}
              unCheckedChildren={INFORMATION.OPTIONS.NO}
              defaultChecked={
                get(initialValues, "project.fields.acceptFees") || false
              }
              onChange={(e) => {
                set(initialValues, "project.fields.acceptFees", e);
                setPercent(initialValues);
              }}
            />
          </div>

          {/* Justifications field */}
          <Input
            type="textarea"
            label={INFORMATION.JUSTIFICATIONS?.LABEL || "Justifications"}
            placeholder={
              INFORMATION.JUSTIFICATIONS?.PLACEHOLDER ||
              "Saisir les justifications"
            }
            icon={<MdOutlineInsertComment size={16} />}
            defaultValue={get(
              initialValues,
              "project.fields.info.justifications",
            )}
            errors={errors["project.fields.info.justifications"]}
            touched={!isEmpty(errors["project.fields.info.justifications"])}
            onChange={({ target: { value } }) => {
              set(initialValues, "project.fields.info.justifications", value);
            }}
            onBlur={() => setPercent(initialValues)}
            autoSize={{ minRows: 2, maxRows: 10 }}
          />

          {/* Comment field */}
          <Input
            type="textarea"
            label={INFORMATION.COMMENT || "Commentaires éventuels"}
            placeholder={INFORMATION.COMMENT || "Saisir vos commentaires"}
            icon={<MdOutlineInsertComment size={16} />}
            defaultValue={get(initialValues, "project.fields.info.comment")}
            errors={errors["project.fields.info.comment"]}
            touched={!isEmpty(errors["project.fields.info.comment"])}
            onChange={({ target: { value } }) => {
              set(initialValues, "project.fields.info.comment", value);
            }}
            onBlur={() => setPercent(initialValues)}
            autoSize={{ minRows: 2, maxRows: 10 }}
          />
        </div>
      </Form>
    </div>
  );
};

export default Information;
